/**
 * Code Editor Stimulus Controller
 *
 * Manages the IDE-like HTML code editor modal lifecycle.
 * Mounts the HtmlCodeEditor Vue component and handles:
 * - Modal open/close with animation
 * - Fullscreen toggle
 * - Save functionality with API integration
 * - Keyboard shortcuts (Cmd+S, Escape, F11)
 * - Lock acquisition/release for collaborative editing
 * - Status bar updates (line/column, char count)
 *
 * Usage:
 *   <div data-controller="admin--code-editor"
 *        data-admin--code-editor-website-id-value="..."
 *        data-admin--code-editor-update-url-value="..."
 *        data-admin--code-editor-csrf-token-value="...">
 */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    websiteId: String,
    updateUrl: String,
    csrfToken: String
  }

  // Current edit context
  currentContext = null

  vueApp = null

  connect() {
    console.log('[CodeEditor] Stimulus controller connected')
    this.bindGlobalFunctions()
    this.bindKeyboardShortcuts()
    console.log('[CodeEditor] window.openCodeModal is now:', typeof window.openCodeModal)
  }

  disconnect() {
    this.unmount()
    this.unbindKeyboardShortcuts()
  }

  /**
   * Expose global functions for backward compatibility with inline JavaScript
   */
  bindGlobalFunctions() {
    window.openCodeModal = this.open.bind(this)
    window.closeCodeModal = this.close.bind(this)
    window.saveBlockHtml = this.save.bind(this)
  }

  /**
   * Bind keyboard shortcuts
   */
  bindKeyboardShortcuts() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
  }

  unbindKeyboardShortcuts() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  /**
   * Handle keyboard shortcuts
   */
  handleKeydown(e) {
    if (!this.element.classList.contains('visible')) return

    // Escape to close (only if not in find/replace)
    if (e.key === 'Escape' && !e.target.closest('.code-find-replace-panel')) {
      e.preventDefault()
      this.close()
      return
    }

    // Cmd+S to save
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault()
      this.save()
      return
    }

    // F11 for fullscreen
    if (e.key === 'F11') {
      e.preventDefault()
      this.toggleFullscreen()
    }
  }

  /**
   * Open the code editor modal for a specific block
   */
  async open(blockId, blockType) {
    console.log('[CodeEditor] open() called with blockId:', blockId, 'blockType:', blockType)
    this.currentContext = { blockId, blockType }

    // Acquire collaborative lock if lock manager exists
    if (window.lockManager) {
      console.log('[CodeEditor] Attempting to acquire lock for block:', blockId)
      const acquired = await window.lockManager.acquireLock(blockId)
      console.log('[CodeEditor] Lock acquisition result:', acquired)
      if (!acquired) {
        const holder = window.lockManager.getLockHolder(blockId)
        console.warn('[CodeEditor] Lock not acquired, holder:', holder)
        window.toastManager?.show(`Block is being edited by ${holder || 'another user'}`, 'error')
        return
      }
    } else {
      console.log('[CodeEditor] No lockManager available, skipping lock acquisition')
    }

    // Update block type badge
    const badgeEl = document.getElementById('codeModalBlockType')
    if (badgeEl) {
      badgeEl.textContent = blockType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase())
    }

    // Get block's current HTML - use innerHTML since outerHTML includes the wrapper
    // which is rendered by the ERB template (app/views/websites/generated/page.html.erb)
    // The rendered_html column stores only the inner content, not the wrapper
    const blockElement = document.querySelector(`[data-block-id="${blockId}"]`)
    console.log('[CodeEditor] Block element found:', !!blockElement, 'blockId:', blockId)
    if (blockElement) {
      console.log('[CodeEditor] Block innerHTML length:', blockElement.innerHTML.length)
    }
    const html = blockElement ? this.formatHtml(blockElement.innerHTML) : ''
    console.log('[CodeEditor] Formatted HTML length:', html.length)

    // Show modal with animation
    const backdrop = document.getElementById('codeModalBackdrop')
    if (backdrop) backdrop.classList.add('visible')
    this.element.classList.add('visible')

    // Mount Vue component
    await this.mountEditor(html)
  }

  /**
   * Mount the HtmlCodeEditor Vue component
   */
  async mountEditor(initialHtml) {
    const mountPoint = document.getElementById('codeEditorMountPoint')
    if (!mountPoint) {
      console.error('[CodeEditor] Mount point not found')
      return
    }

    try {
      const [{ createApp }, { default: HtmlCodeEditor }] = await Promise.all([
        import('vue'),
        import('~/javascript/components/admin/page_editor/HtmlCodeEditor.vue')
      ])

      this.vueApp = createApp(HtmlCodeEditor, {
        initialValue: initialHtml,
        websiteId: this.websiteIdValue,
        onSave: () => this.save(),
        onClose: () => this.close(),
        onCursorChange: (pos) => this.updateStatusBar(pos)
      })

      this.vueApp.mount(mountPoint)
    } catch (err) {
      console.error('[CodeEditor] Failed to mount Vue component:', err)
    }
  }

  /**
   * Unmount Vue component
   */
  unmount() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  /**
   * Close the code editor modal
   */
  close() {
    // Release collaborative lock
    if (window.lockManager && this.currentContext?.blockId) {
      window.lockManager.releaseLock(this.currentContext.blockId)
    }

    // Hide modal with animation
    const backdrop = document.getElementById('codeModalBackdrop')
    if (backdrop) backdrop.classList.remove('visible')
    this.element.classList.remove('visible')
    this.element.classList.remove('fullscreen')

    // Unmount Vue
    this.unmount()

    // Reset button state
    const saveBtn = document.getElementById('codeSaveBtn')
    if (saveBtn) saveBtn.disabled = false

    this.currentContext = null
  }

  /**
   * Toggle fullscreen mode
   */
  toggleFullscreen() {
    this.element.classList.toggle('fullscreen')

    // Update fullscreen button icon/state
    const btn = document.getElementById('codeModalFullscreenBtn')
    if (btn) {
      btn.setAttribute('aria-pressed', this.element.classList.contains('fullscreen'))
    }
  }

  /**
   * Save the edited HTML
   */
  async save() {
    if (!this.currentContext || !this.vueApp) return

    // Get HTML from Vue component
    const html = this.vueApp._instance?.exposed?.getHtml?.()

    if (!html?.trim()) {
      alert('Please enter some HTML content.')
      return
    }

    const saveBtn = document.getElementById('codeSaveBtn')
    if (saveBtn) saveBtn.disabled = true

    // Update status indicator
    if (window.updateStatus) {
      window.updateStatus('Saving HTML...', 'saving')
    }

    try {
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfTokenValue,
          Accept: 'application/json'
        },
        body: JSON.stringify({
          block_id: this.currentContext.blockId,
          html
        })
      })

      // Handle locked response
      if (response.status === 423) {
        const error = await response.json()
        throw new Error(`Block is locked by ${error.locked_by?.name || 'another user'}`)
      }

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save HTML')
      }

      // Store for undo functionality
      if (window.lastEditedBlock !== undefined) {
        window.lastEditedBlock = {
          blockId: this.currentContext.blockId,
          previousVersion: result.version - 1
        }
      }

      // Enable undo button
      const undoBtn = document.getElementById('undoBtn')
      if (undoBtn) undoBtn.disabled = false

      // Close modal
      this.close()

      // Update status
      if (window.updateStatus) {
        window.updateStatus('HTML saved!', 'saved')
      }

      // Update DOM without full reload
      if (result.rendered_html && window.updateBlockInDom) {
        window.updateBlockInDom(
          this.currentContext.blockId,
          result.rendered_html,
          result.lock_version,
          result.version
        )
        if (window.showToolbarToast) {
          window.showToolbarToast('HTML saved!', 'success')
        }
      } else {
        // Fallback to reload
        setTimeout(() => window.location.reload(), 500)
      }
    } catch (error) {
      console.error('[CodeEditor] Save error:', error)
      alert(`Failed to save: ${error.message}`)

      if (saveBtn) saveBtn.disabled = false

      if (window.updateStatus) {
        window.updateStatus('Save failed', 'error')
      }
    }
  }

  /**
   * Update the status bar with cursor position and character count
   */
  updateStatusBar({ line, col, chars }) {
    const positionEl = document.getElementById('codeEditorPosition')
    const charCountEl = document.getElementById('codeEditorCharCount')

    if (positionEl) {
      positionEl.textContent = `Ln ${line}, Col ${col}`
    }
    if (charCountEl) {
      charCountEl.textContent = `${chars.toLocaleString()} chars`
    }
  }

  /**
   * Strip any wrapper div elements that have data-block-id attribute.
   * This handles corrupted data where outerHTML was previously saved as rendered_html,
   * creating nested wrappers. We unwrap to get the actual block content.
   */
  stripWrapperIfPresent(html) {
    if (!html) return ''

    // Create a temporary container to parse the HTML
    const temp = document.createElement('div')
    temp.innerHTML = html.trim()

    // Check if the first (and only) child is a wrapper div with data-block-id
    // This would indicate a corrupted nested wrapper situation
    if (temp.children.length === 1) {
      const firstChild = temp.children[0]
      if (firstChild.tagName === 'DIV' && firstChild.hasAttribute('data-block-id')) {
        // This is a wrapper div that shouldn't be here - return its innerHTML instead
        console.log('[CodeEditor] Detected nested wrapper, unwrapping content')
        return firstChild.innerHTML.trim()
      }
    }

    return html
  }

  /**
   * Format HTML for display with proper indentation
   * This is a simplified version - js-beautify in the Vue component handles full formatting
   */
  formatHtml(html) {
    if (!html) return ''

    // First strip any wrapper elements from corrupted data
    html = this.stripWrapperIfPresent(html)

    // Basic pretty-print with indentation
    let result = ''
    let indent = 0
    const tab = '  '

    // Split on tags while preserving them
    const tokens = html.split(/(<[^>]+>)/g).filter(Boolean)

    for (const token of tokens) {
      if (token.startsWith('</')) {
        // Closing tag - decrease indent first
        indent = Math.max(0, indent - 1)
        result += `${tab.repeat(indent)}${token.trim()}\n`
      } else if (token.startsWith('<') && !token.startsWith('<!')) {
        // Opening tag
        result += `${tab.repeat(indent)}${token.trim()}\n`

        // Self-closing tags don't increase indent
        if (!token.match(/<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr|!)[^>]*>/i) &&
            !token.endsWith('/>')) {
          indent++
        }
      } else {
        // Text content
        const trimmed = token.trim()
        if (trimmed) {
          result += `${tab.repeat(indent)}${trimmed}\n`
        }
      }
    }

    return result.trim()
  }
}
